/**
 * Google Analytics 4 — centralized event tracking utility.
 *
 * Usage:
 *   import { trackEvent } from "../utils/analytics";
 *   trackEvent("login", { method: "email" });
 *
 * All calls are no-ops when VITE_GA_ID is not configured so dev/test
 * environments stay clean.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

/** Typed gtag shim — window.gtag is injected by the GA4 loader in index.tsx */
function gtag(...args: any[]): void {
  if (typeof window !== "undefined" && typeof (window as any).gtag === "function") {
    (window as any).gtag(...args);
  } else if (typeof window !== "undefined" && (window as any).dataLayer) {
    // Fallback: push directly to dataLayer (same thing gtag() does internally)
    (window as any).dataLayer.push(args);
  }
}

/** Returns true only when a GA measurement ID has been injected */
function isAnalyticsEnabled(): boolean {
  return !!import.meta.env.VITE_GA_ID;
}

// ---------------------------------------------------------------------------
// Generic event helper
// ---------------------------------------------------------------------------

export function trackEvent(eventName: string, params?: Record<string, unknown>): void {
  if (!isAnalyticsEnabled()) return;
  gtag("event", eventName, params ?? {});
}

// ---------------------------------------------------------------------------
// Authentication events
// ---------------------------------------------------------------------------

/** Fire after a successful sign-in (email/password or OAuth) */
export function trackLogin(method: "email" | "google" = "email"): void {
  trackEvent("login", { method });
}

/** Fire after a successful sign-up / account confirmation */
export function trackSignUp(method: "email" | "google" = "email"): void {
  trackEvent("sign_up", { method });
}

// ---------------------------------------------------------------------------
// Course & enrollment events
// ---------------------------------------------------------------------------

/** User viewed a course details page — treated as a page_view for /courses */
export function trackCourseView(courseId: number | string, courseName: string): void {
  trackEvent("course_view", { course_id: String(courseId), course_name: courseName });
}

/** User enrolled in a free course */
export function trackCourseEnrollment(
  courseId: number | string,
  courseName: string,
  courseLevel?: string
): void {
  trackEvent("course_enrollment", {
    course_id: String(courseId),
    course_name: courseName,
    course_level: courseLevel ?? "",
  });
}

/** User clicked "Buy now" — begin_checkout */
export function trackBeginCheckout(
  courseId: number | string,
  courseName: string,
  packageName: string,
  price?: number | string
): void {
  const numericPrice = typeof price === "string" ? parseFloat(price) : (price ?? 0);
  trackEvent("begin_checkout", {
    currency: "EUR",
    value: numericPrice,
    items: [
      {
        item_id: String(courseId),
        item_name: courseName,
        item_variant: packageName,
        quantity: 1,
        price: numericPrice,
      },
    ],
  });
}

/** Purchase confirmed on the pay-success page */
export function trackPurchase(
  courseId: number | string,
  courseName: string,
  packageName: string,
  price?: number,
  transactionId?: string
): void {
  trackEvent("purchase", {
    transaction_id: transactionId ?? `course_${courseId}_${Date.now()}`,
    currency: "EUR",
    value: price ?? 0,
    items: [
      {
        item_id: String(courseId),
        item_name: courseName,
        item_variant: packageName,
        quantity: 1,
        price: price ?? 0,
      },
    ],
  });
}

// ---------------------------------------------------------------------------
// Exercise & learning events
// ---------------------------------------------------------------------------

/** User completed (submitted) an exercise */
export function trackExerciseComplete(params: {
  exerciseId: number | string;
  exerciseName?: string;
  exerciseType?: string;
  courseId?: number | string;
  courseName?: string;
  moduleId?: number | string;
  moduleName?: string;
  score?: number;
  maxScore?: number;
}): void {
  trackEvent("exercise_complete", {
    exercise_id: String(params.exerciseId),
    exercise_name: params.exerciseName ?? "",
    exercise_type: params.exerciseType ?? "",
    course_id: params.courseId ? String(params.courseId) : "",
    course_name: params.courseName ?? "",
    module_id: params.moduleId ? String(params.moduleId) : "",
    module_name: params.moduleName ?? "",
    score: params.score ?? 0,
    max_score: params.maxScore ?? 0,
  });
}

/** User completed all exercises in a module */
export function trackModuleComplete(params: {
  moduleId: number | string;
  moduleName?: string;
  courseId?: number | string;
  courseName?: string;
}): void {
  trackEvent("module_complete", {
    module_id: String(params.moduleId),
    module_name: params.moduleName ?? "",
    course_id: params.courseId ? String(params.courseId) : "",
    course_name: params.courseName ?? "",
  });
}

/** Onboarding tour completed */
export function trackTutorialComplete(): void {
  trackEvent("tutorial_complete");
}

// ---------------------------------------------------------------------------
// Vocabulary events
// ---------------------------------------------------------------------------

/** User added a word to their vocabulary list */
export function trackVocabWordAdded(word: string): void {
  trackEvent("vocab_word_added", { word });
}

// ---------------------------------------------------------------------------
// AI chat events
// ---------------------------------------------------------------------------

/** User sent a message to the AI tutor */
export function trackAIChatMessage(moduleId?: number | string): void {
  trackEvent("ai_chat_message", {
    module_id: moduleId ? String(moduleId) : "",
  });
}

// ---------------------------------------------------------------------------
// User properties
// ---------------------------------------------------------------------------

/**
 * Set GA4 user properties for segmentation.
 * Call this after login / session restore when user data is available.
 */
export function setUserProperties(params: {
  userId?: string;
  userRole?: "ADMIN" | "STUDENT" | "CURATOR" | string;
  subscriptionTier?: "free" | "pro" | string;
  preferredLanguage?: string;
}): void {
  if (!isAnalyticsEnabled()) return;

  // Set user ID for cross-device tracking
  if (params.userId) {
    gtag("config", import.meta.env.VITE_GA_ID, {
      user_id: params.userId,
    });
  }

  // Set custom dimensions
  gtag("event", "user_properties_set", {
    user_role: params.userRole?.toLowerCase() ?? "",
    subscription_tier: params.subscriptionTier?.toLowerCase() ?? "free",
    preferred_language: params.preferredLanguage ?? "",
  });
}
